import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Users, Clock, TrendingUp, ArrowLeft, ChevronDown, ChevronUp, Lightbulb, MapPin, FileDown, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnalyticsDashboard = () => {
  const [overview, setOverview] = useState([]);
  const [selectedTour, setSelectedTour] = useState(null);
  const [tourDetail, setTourDetail] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [savedReports, setSavedReports] = useState([]);
  const [showSavedReports, setShowSavedReports] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const navigate = useNavigate();
  const { token, user } = useAuth();

  useEffect(() => {
    if (token && user?.account_type === 'admin') {
      fetchOverview();
    }
  }, [token, user]);

  const fetchOverview = async () => {
    try {
      const res = await axios.get(`${API}/admin/analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOverview(res.data);
    } catch (err) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedReports = async () => {
    try {
      const res = await axios.get(`${API}/admin/analytics/reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedReports(res.data);
    } catch (err) {
      console.error('Failed to load saved reports');
    }
  };

  const deleteSavedReport = async (insightId) => {
    try {
      await axios.delete(`${API}/admin/analytics/reports/${insightId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedReports(prev => prev.filter(r => r.insight_id !== insightId));
      toast.success('Report deleted');
    } catch (err) {
      toast.error('Failed to delete report');
    }
  };

  const fetchTourDetail = async (tourId) => {
    setDetailLoading(true);
    setAiInsights(null);
    try {
      const res = await axios.get(`${API}/admin/analytics/tour/${tourId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTourDetail(res.data);
      setSelectedTour(tourId);
    } catch (err) {
      toast.error('Failed to load tour analytics');
    } finally {
      setDetailLoading(false);
    }
  };

  const generateAiInsights = async (tourId) => {
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/admin/analytics/tour/${tourId}/generate-insights`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAiInsights(res.data);
      toast.success('AI insights generated!');
    } catch (err) {
      toast.error('Failed to generate insights');
    } finally {
      setAiLoading(false);
    }
  };

  // Phase 2B: server-rendered premium PDF (12-section consulting-grade report)
  const [premiumLoading, setPremiumLoading] = useState(false);
  const generatePremiumReport = async (tourId, tourTitle) => {
    const clientName = window.prompt(
      'Client name for this report (e.g., "Tel Aviv Museum"):',
      'Audiostride Internal'
    );
    if (clientName === null) return;
    const days = window.prompt('Reporting period in days:', '30');
    if (days === null) return;
    const periodDays = parseInt(days, 10) || 30;

    setPremiumLoading(true);
    try {
      const end = new Date();
      const start = new Date(end.getTime() - periodDays * 24 * 60 * 60 * 1000);
      toast.info('Generating premium report — this may take 10-20 seconds (AI insights running)…', { duration: 8000 });
      const res = await axios.post(
        `${API}/admin/analytics/premium-report/${tourId}`,
        {
          start: start.toISOString(),
          end: end.toISOString(),
          client_name: clientName,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safeClient = clientName.replace(/\s+/g, '_');
      const safeTour = (tourTitle || 'tour').replace(/\s+/g, '_');
      a.href = url;
      a.download = `${end.toISOString().slice(0, 10)}_${safeClient}_${safeTour}_Analytics.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Premium report downloaded!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate premium report');
    } finally {
      setPremiumLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0m';
    const mins = Math.floor(seconds / 60);
    const hrs = Math.floor(mins / 60);
    if (hrs > 0) return `${hrs}h ${mins % 60}m`;
    return `${mins}m`;
  };

  const generatePDFReport = async (tourData, aiData) => {
    if (!tourData) return;
    
    toast.info('Generating report...');
    
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - margin * 2;
    let y = 20;
    
    // Load logo
    try {
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        logoImg.onload = resolve;
        logoImg.onerror = reject;
        logoImg.src = '/audiostride-logo.png';
      });
      
      // Draw logo (centered, max 60mm wide)
      const logoRatio = logoImg.height / logoImg.width;
      const logoWidth = Math.min(60, contentWidth * 0.4);
      const logoHeight = logoWidth * logoRatio;
      doc.addImage(logoImg, 'PNG', (pageWidth - logoWidth) / 2, y, logoWidth, logoHeight);
      y += logoHeight + 8;
    } catch (e) {
      // Fallback text if logo fails
      doc.setFontSize(24);
      doc.setTextColor(20, 184, 166);
      doc.text('Audiostride', pageWidth / 2, y + 10, { align: 'center' });
      y += 18;
    }
    
    // Report title
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text('Tour Analytics Report', pageWidth / 2, y, { align: 'center' });
    y += 5;
    doc.text(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), pageWidth / 2, y, { align: 'center' });
    y += 3;
    
    // Divider
    doc.setDrawColor(226, 232, 240);
    doc.line(margin, y, pageWidth - margin, y);
    y += 10;
    
    // Tour name
    doc.setFontSize(18);
    doc.setTextColor(15, 23, 42);
    doc.text(tourData.tour.title, margin, y);
    y += 6;
    doc.setFontSize(11);
    doc.setTextColor(100, 116, 139);
    doc.text(tourData.tour.city || '', margin, y);
    y += 12;
    
    // Core metrics boxes
    const metrics = [
      { label: 'Total Starts', value: String(tourData.core_metrics.total_starts) },
      { label: 'Completions', value: String(tourData.core_metrics.total_completions) },
      { label: 'Unique Users', value: String(tourData.core_metrics.unique_users) },
      { label: 'Completion Rate', value: `${tourData.core_metrics.completion_rate}%` },
    ];
    
    const boxWidth = (contentWidth - 9) / 4;
    metrics.forEach((m, i) => {
      const x = margin + i * (boxWidth + 3);
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(x, y, boxWidth, 22, 2, 2, 'F');
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(m.label, x + boxWidth / 2, y + 7, { align: 'center' });
      doc.setFontSize(16);
      doc.setTextColor(15, 23, 42);
      doc.text(m.value, x + boxWidth / 2, y + 17, { align: 'center' });
    });
    y += 30;
    
    // Key Insights (basic)
    if (tourData.insights.length > 0) {
      doc.setFillColor(255, 251, 235);
      const insightHeight = 8 + tourData.insights.length * 6;
      doc.roundedRect(margin, y, contentWidth, insightHeight, 2, 2, 'F');
      doc.setFontSize(11);
      doc.setTextColor(146, 64, 14);
      doc.text('Key Insights', margin + 5, y + 7);
      doc.setFontSize(9);
      doc.setTextColor(120, 53, 15);
      tourData.insights.forEach((insight, i) => {
        const insightY = y + 14 + i * 6;
        if (insightY < 270) {
          doc.text(`• ${insight}`, margin + 5, insightY);
        }
      });
      y += insightHeight + 8;
    }
    
    // AI-Generated Sections (premium content)
    if (aiData) {
      // Executive Summary
      if (aiData.executive_summary) {
        if (y > 220) { doc.addPage(); y = 20; }
        doc.setFillColor(238, 242, 255);
        const summaryLines = doc.splitTextToSize(aiData.executive_summary, contentWidth - 10);
        const summaryHeight = 12 + summaryLines.length * 5;
        doc.roundedRect(margin, y, contentWidth, summaryHeight, 2, 2, 'F');
        doc.setFontSize(11);
        doc.setTextColor(55, 48, 163);
        doc.text('Executive Summary', margin + 5, y + 7);
        doc.setFontSize(9);
        doc.setTextColor(67, 56, 202);
        doc.text(summaryLines, margin + 5, y + 14);
        y += summaryHeight + 8;
      }
      
      // Revenue Opportunities
      if (aiData.revenue_opportunities?.length > 0) {
        if (y > 210) { doc.addPage(); y = 20; }
        doc.setFillColor(240, 253, 244);
        const revItems = aiData.revenue_opportunities;
        const revHeight = 12 + revItems.length * 12;
        doc.roundedRect(margin, y, contentWidth, Math.min(revHeight, 80), 2, 2, 'F');
        doc.setFontSize(11);
        doc.setTextColor(22, 101, 52);
        doc.text('Revenue Opportunities', margin + 5, y + 7);
        doc.setFontSize(8);
        doc.setTextColor(21, 128, 61);
        let revY = y + 14;
        revItems.forEach((item) => {
          if (revY < 268) {
            const lines = doc.splitTextToSize(`$ ${item}`, contentWidth - 12);
            doc.text(lines, margin + 5, revY);
            revY += lines.length * 4 + 3;
          }
        });
        y = revY + 5;
      }
      
      // Experience Improvements
      if (aiData.experience_improvements?.length > 0) {
        if (y > 210) { doc.addPage(); y = 20; }
        doc.setFillColor(239, 246, 255);
        const expItems = aiData.experience_improvements;
        const expHeight = 12 + expItems.length * 12;
        doc.roundedRect(margin, y, contentWidth, Math.min(expHeight, 80), 2, 2, 'F');
        doc.setFontSize(11);
        doc.setTextColor(30, 64, 175);
        doc.text('Experience Improvements', margin + 5, y + 7);
        doc.setFontSize(8);
        doc.setTextColor(37, 99, 235);
        let expY = y + 14;
        expItems.forEach((item) => {
          if (expY < 268) {
            const lines = doc.splitTextToSize(`• ${item}`, contentWidth - 12);
            doc.text(lines, margin + 5, expY);
            expY += lines.length * 4 + 3;
          }
        });
        y = expY + 5;
      }
      
      // Key Findings
      if (aiData.key_findings?.length > 0) {
        if (y > 220) { doc.addPage(); y = 20; }
        doc.setFontSize(11);
        doc.setTextColor(15, 23, 42);
        doc.text('Detailed Findings', margin, y);
        y += 6;
        doc.setFontSize(8);
        doc.setTextColor(71, 85, 105);
        aiData.key_findings.forEach((finding) => {
          if (y > 270) { doc.addPage(); y = 20; }
          const lines = doc.splitTextToSize(`• ${finding}`, contentWidth - 5);
          doc.text(lines, margin + 3, y);
          y += lines.length * 4 + 2;
        });
        y += 5;
      }
    }
    
    // Stop Performance
    if (y > 230) { doc.addPage(); y = 20; }
    doc.setFontSize(13);
    doc.setTextColor(15, 23, 42);
    doc.text('Stop Performance', margin, y);
    y += 8;
    
    // Table header
    doc.setFillColor(241, 245, 249);
    doc.rect(margin, y, contentWidth, 7, 'F');
    doc.setFontSize(7);
    doc.setTextColor(71, 85, 105);
    doc.text('#', margin + 2, y + 5);
    doc.text('Stop Name', margin + 10, y + 5);
    doc.text('Played', margin + 100, y + 5);
    doc.text('Completed', margin + 118, y + 5);
    doc.text('Drop-off', margin + 142, y + 5);
    y += 9;
    
    doc.setFontSize(8);
    tourData.stop_metrics.forEach((stop) => {
      if (y > 270) { doc.addPage(); y = 20; }
      doc.setTextColor(71, 85, 105);
      doc.text(String(stop.stop_number), margin + 2, y + 4);
      doc.setTextColor(15, 23, 42);
      const title = stop.title.length > 40 ? stop.title.substring(0, 37) + '...' : stop.title;
      doc.text(title, margin + 10, y + 4);
      doc.text(String(stop.times_played), margin + 103, y + 4);
      doc.text(String(stop.times_completed), margin + 125, y + 4);
      
      // Color code drop-off
      if (stop.drop_off_rate > 30) doc.setTextColor(239, 68, 68);
      else if (stop.drop_off_rate > 15) doc.setTextColor(245, 158, 11);
      else doc.setTextColor(20, 184, 166);
      doc.text(`${stop.drop_off_rate}%`, margin + 145, y + 4);
      
      // Row divider
      doc.setDrawColor(241, 245, 249);
      doc.line(margin, y + 6, pageWidth - margin, y + 6);
      y += 8;
    });
    y += 5;
    
    // Entry Points & Devices
    if (y > 240) { doc.addPage(); y = 20; }
    
    const epEntries = Object.entries(tourData.entry_points || {});
    const devEntries = Object.entries(tourData.devices || {});
    
    if (epEntries.length > 0 || devEntries.length > 0) {
      const halfWidth = contentWidth / 2 - 3;
      
      if (epEntries.length > 0) {
        doc.setFontSize(10);
        doc.setTextColor(15, 23, 42);
        doc.text('How Users Find This Tour', margin, y);
        y += 6;
        doc.setFontSize(8);
        epEntries.forEach(([source, count]) => {
          doc.setTextColor(71, 85, 105);
          doc.text(source.charAt(0).toUpperCase() + source.slice(1), margin + 3, y);
          doc.setTextColor(15, 23, 42);
          doc.text(String(count), margin + halfWidth - 5, y, { align: 'right' });
          y += 5;
        });
        y += 5;
      }
      
      if (devEntries.length > 0) {
        doc.setFontSize(10);
        doc.setTextColor(15, 23, 42);
        doc.text('Devices', margin, y);
        y += 6;
        doc.setFontSize(8);
        devEntries.forEach(([device, count]) => {
          doc.setTextColor(71, 85, 105);
          doc.text(device.charAt(0).toUpperCase() + device.slice(1), margin + 3, y);
          doc.setTextColor(15, 23, 42);
          doc.text(String(count), margin + halfWidth - 5, y, { align: 'right' });
          y += 5;
        });
      }
    }
    
    // Save
    const fileName = `Audiostride_${tourData.tour.title.replace(/[^a-zA-Z0-9]/g, '_')}_Report_${new Date().toISOString().split('T')[0]}.pdf`;
    
    // Capture charts from the DOM if they exist
    const chartsEl = document.getElementById('analytics-charts');
    if (chartsEl) {
      doc.addPage();
      let chartY = 15;
      
      // Title
      doc.setFontSize(14);
      doc.setTextColor(15, 23, 42);
      doc.text('Visual Analytics', margin, chartY);
      chartY += 10;
      
      try {
        const canvas = await html2canvas(chartsEl, { 
          scale: 2, 
          backgroundColor: '#ffffff',
          logging: false
        });
        const imgData = canvas.toDataURL('image/jpeg', 0.8);
        const imgWidth = contentWidth;
        const imgHeight = (canvas.height / canvas.width) * imgWidth;
        
        // Split across pages if needed
        const maxHeight = 260;
        if (imgHeight <= maxHeight) {
          doc.addImage(imgData, 'JPEG', margin, chartY, imgWidth, imgHeight);
        } else {
          // Scale to fit one page
          const scale = maxHeight / imgHeight;
          doc.addImage(imgData, 'JPEG', margin, chartY, imgWidth * scale, maxHeight);
        }
      } catch (e) {
        doc.setFontSize(9);
        doc.setTextColor(148, 163, 184);
        doc.text('Charts could not be captured for this report.', margin, chartY);
      }
    }
    
    // Footer on each page
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(148, 163, 184);
      doc.text('Generated by Audiostride Analytics', pageWidth / 2, 290, { align: 'center' });
      doc.text(`audiostride.com | Page ${i} of ${totalPages}`, pageWidth / 2, 294, { align: 'center' });
    }
    
    // Save
    doc.save(fileName);
    toast.success('Report downloaded!');
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-teal-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <button onClick={() => navigate('/admin')} className="flex items-center text-teal-400 hover:text-teal-300 mb-4">
            <ArrowLeft className="w-5 h-5 mr-1" /> Admin
          </button>
          <div className="flex items-center gap-3">
            <BarChart3 className="w-10 h-10 text-teal-400" />
            <div>
              <h1 className="text-3xl font-bold" data-testid="analytics-title">Analytics</h1>
              <p className="text-slate-400">Tour performance and user engagement</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Overview Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200">
            <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
              <TrendingUp className="w-4 h-4" />
              Total Starts
            </div>
            <p className="text-3xl font-black text-slate-900" data-testid="total-starts">
              {overview.reduce((sum, t) => sum + t.total_starts, 0)}
            </p>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200">
            <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
              <BarChart3 className="w-4 h-4" />
              Completions
            </div>
            <p className="text-3xl font-black text-slate-900" data-testid="total-completions">
              {overview.reduce((sum, t) => sum + t.total_completions, 0)}
            </p>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200">
            <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
              <Users className="w-4 h-4" />
              Unique Users
            </div>
            <p className="text-3xl font-black text-slate-900" data-testid="total-users">
              {overview.reduce((sum, t) => sum + t.unique_users, 0)}
            </p>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200">
            <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
              <Clock className="w-4 h-4" />
              Avg Completion
            </div>
            <p className="text-3xl font-black text-teal-600" data-testid="avg-completion">
              {overview.length > 0 ? Math.round(overview.reduce((sum, t) => sum + t.completion_rate, 0) / overview.length) : 0}%
            </p>
          </div>
        </div>

        {/* Tour List */}
        <h2 className="text-xl font-bold text-slate-900 mb-4">Tour Performance</h2>
        {overview.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center border border-slate-200">
            <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No analytics data yet. Data will appear as users take tours.</p>
          </div>
        ) : (
          <div className="space-y-3 mb-8">
            {overview.map((tour) => (
              <div key={tour.tour_id}>
                <button
                  onClick={() => selectedTour === tour.tour_id ? setSelectedTour(null) : fetchTourDetail(tour.tour_id)}
                  className={`w-full bg-white rounded-xl p-5 border-2 transition-all text-left hover:shadow-md ${
                    selectedTour === tour.tour_id ? 'border-teal-400 shadow-md' : 'border-slate-200'
                  }`}
                  data-testid={`tour-analytics-${tour.tour_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-slate-900 text-lg">{tour.title}</h3>
                      <p className="text-slate-500 text-sm">{tour.city}</p>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <div className="text-center">
                        <p className="font-bold text-slate-900">{tour.total_starts}</p>
                        <p className="text-slate-500">starts</p>
                      </div>
                      <div className="text-center">
                        <p className="font-bold text-teal-600">{tour.completion_rate}%</p>
                        <p className="text-slate-500">complete</p>
                      </div>
                      <div className="text-center">
                        <p className="font-bold text-slate-900">{tour.unique_users}</p>
                        <p className="text-slate-500">users</p>
                      </div>
                      {selectedTour === tour.tour_id ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                    </div>
                  </div>
                </button>

                {/* Expanded Detail */}
                {selectedTour === tour.tour_id && tourDetail && !detailLoading && (
                  <div className="bg-white border-2 border-teal-400 border-t-0 rounded-b-xl p-6 -mt-2">
                    {/* Report Actions */}
                    <div className="flex gap-2 mb-6 flex-wrap">
                      <button
                        onClick={() => generatePDFReport(tourDetail, aiInsights)}
                        data-testid="download-report-button"
                        className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-all active:scale-95"
                      >
                        <FileDown className="w-4 h-4" />
                        Download PDF Report
                      </button>
                      <button
                        onClick={() => generateAiInsights(tour.tour_id)}
                        disabled={aiLoading}
                        data-testid="generate-ai-insights-button"
                        className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-all active:scale-95"
                      >
                        <Lightbulb className="w-4 h-4" />
                        {aiLoading ? 'Generating...' : 'Generate AI Report'}
                      </button>
                      <button
                        onClick={() => generatePremiumReport(tour.tour_id, tour.title)}
                        disabled={premiumLoading}
                        data-testid="generate-premium-report-button"
                        className="flex items-center gap-2 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 hover:from-amber-600 hover:via-orange-600 hover:to-rose-600 disabled:opacity-60 text-white px-4 py-2 rounded-lg font-bold text-sm shadow-lg transition-all active:scale-95"
                        title="12-section consulting-grade report (B2B premium)"
                      >
                        <FileDown className="w-4 h-4" />
                        {premiumLoading ? 'Generating Premium…' : '⭐ Generate Premium Report (B2B)'}
                      </button>
                    </div>

                    {/* AI-Generated Insights */}
                    {aiInsights && (
                      <div className="mb-6 space-y-4">
                        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-5">
                          <h4 className="font-bold text-indigo-900 text-lg mb-2">Executive Summary</h4>
                          <p className="text-indigo-800 text-sm leading-relaxed">{aiInsights.executive_summary}</p>
                        </div>
                        
                        {aiInsights.key_findings?.length > 0 && (
                          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                            <h4 className="font-bold text-slate-800 mb-2">Key Findings</h4>
                            <ul className="space-y-1">
                              {aiInsights.key_findings.map((f, i) => (
                                <li key={i} className="text-slate-700 text-sm flex items-start gap-2">
                                  <span className="text-indigo-400 mt-0.5 flex-shrink-0">-</span> {f}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {aiInsights.revenue_opportunities?.length > 0 && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <h4 className="font-bold text-green-800 mb-2">Revenue Opportunities</h4>
                            <ul className="space-y-1">
                              {aiInsights.revenue_opportunities.map((r, i) => (
                                <li key={i} className="text-green-700 text-sm flex items-start gap-2">
                                  <span className="text-green-400 mt-0.5 flex-shrink-0">$</span> {r}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {aiInsights.experience_improvements?.length > 0 && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <h4 className="font-bold text-blue-800 mb-2">Experience Improvements</h4>
                            <ul className="space-y-1">
                              {aiInsights.experience_improvements.map((e, i) => (
                                <li key={i} className="text-blue-700 text-sm flex items-start gap-2">
                                  <span className="text-blue-400 mt-0.5 flex-shrink-0">-</span> {e}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {aiInsights.period_comparison && (
                          <p className="text-slate-500 text-xs italic">{aiInsights.period_comparison}</p>
                        )}
                      </div>
                    )}

                    {/* Insights */}
                    {tourDetail.insights.length > 0 && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                        <div className="flex items-center gap-2 mb-2">
                          <Lightbulb className="w-5 h-5 text-amber-600" />
                          <h4 className="font-bold text-amber-800">Key Insights</h4>
                        </div>
                        <ul className="space-y-1">
                          {tourDetail.insights.map((insight, i) => (
                            <li key={i} className="text-amber-700 text-sm flex items-start gap-2">
                              <span className="text-amber-400 mt-1">-</span>
                              {insight}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Charts */}
                    {tourDetail.charts && (
                      <div className="mb-6 space-y-6" id="analytics-charts">
                        {/* 1. Engagement Over Time */}
                        <div className="bg-white border border-slate-200 rounded-lg p-4">
                          <h4 className="font-bold text-slate-800 mb-3">Engagement Over Time</h4>
                          <ResponsiveContainer width="100%" height={200}>
                            <LineChart data={tourDetail.charts.engagement_over_time}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={4} stroke="#94a3b8" />
                              <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" allowDecimals={false} />
                              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                              <Line type="monotone" dataKey="starts" stroke="#14b8a6" strokeWidth={2} dot={false} name="Starts" />
                              <Line type="monotone" dataKey="completions" stroke="#6366f1" strokeWidth={2} dot={false} name="Completions" />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>

                        {/* 2. Audience Retention */}
                        {tourDetail.charts.audience_retention.length > 0 && (
                          <div className="bg-white border border-slate-200 rounded-lg p-4">
                            <h4 className="font-bold text-slate-800 mb-1">Audience Retention</h4>
                            <p className="text-slate-500 text-xs mb-3">% of users still engaged at each stop</p>
                            <ResponsiveContainer width="100%" height={200}>
                              <AreaChart data={tourDetail.charts.audience_retention}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis dataKey="stop" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                                <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" domain={[0, 100]} unit="%" />
                                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v) => `${v}%`} />
                                <Area type="monotone" dataKey="retention" stroke="#f59e0b" fill="#fef3c7" strokeWidth={2} name="Retention" />
                              </AreaChart>
                            </ResponsiveContainer>
                          </div>
                        )}

                        {/* 3. Time of Day Distribution */}
                        <div className="bg-white border border-slate-200 rounded-lg p-4">
                          <h4 className="font-bold text-slate-800 mb-1">When Users Take Tours</h4>
                          <p className="text-slate-500 text-xs mb-3">Tour starts by hour of day</p>
                          <ResponsiveContainer width="100%" height={180}>
                            <BarChart data={tourDetail.charts.time_distribution}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="hour" tick={{ fontSize: 9 }} interval={2} stroke="#94a3b8" />
                              <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" allowDecimals={false} />
                              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                              <Bar dataKey="count" name="Starts" radius={[4, 4, 0, 0]}>
                                {tourDetail.charts.time_distribution.map((entry, index) => (
                                  <Cell key={index} fill={
                                    entry.period === 'Morning' ? '#34d399' :
                                    entry.period === 'Afternoon' ? '#fbbf24' :
                                    entry.period === 'Evening' ? '#f97316' : '#6366f1'
                                  } />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                          <div className="flex justify-center gap-4 mt-2 text-xs">
                            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-400 inline-block"></span> Morning</span>
                            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-400 inline-block"></span> Afternoon</span>
                            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-500 inline-block"></span> Evening</span>
                            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-indigo-500 inline-block"></span> Night</span>
                          </div>
                        </div>

                        {/* 4. Stop Engagement */}
                        {tourDetail.charts.stop_engagement.length > 0 && (
                          <div className="bg-white border border-slate-200 rounded-lg p-4">
                            <h4 className="font-bold text-slate-800 mb-1">Stop Engagement</h4>
                            <p className="text-slate-500 text-xs mb-3">Average listen time per stop (seconds)</p>
                            <ResponsiveContainer width="100%" height={Math.max(200, tourDetail.charts.stop_engagement.length * 28)}>
                              <BarChart data={tourDetail.charts.stop_engagement} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis type="number" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                                <YAxis dataKey="title" type="category" tick={{ fontSize: 9 }} width={120} stroke="#94a3b8" />
                                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                                <Bar dataKey="avg_listen" name="Avg Listen (s)" radius={[0, 4, 4, 0]}>
                                  {tourDetail.charts.stop_engagement.map((entry, index) => (
                                    <Cell key={index} fill={entry.drop_off > 30 ? '#ef4444' : entry.drop_off > 15 ? '#f59e0b' : '#14b8a6'} />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                            <div className="flex justify-center gap-4 mt-2 text-xs">
                              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-teal-500 inline-block"></span> Low drop-off</span>
                              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-500 inline-block"></span> Moderate</span>
                              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500 inline-block"></span> High drop-off</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Stop Performance */}
                    <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> Stop Performance
                    </h4>
                    <div className="space-y-2 mb-6">
                      {tourDetail.stop_metrics.map((stop) => {
                        const maxPlayed = Math.max(...tourDetail.stop_metrics.map(s => s.times_played), 1);
                        const barWidth = (stop.times_played / maxPlayed) * 100;
                        return (
                          <div key={stop.stop_id} className="flex items-center gap-3">
                            <span className="text-xs text-slate-500 w-6 text-right flex-shrink-0">{stop.stop_number}</span>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium text-slate-700 truncate">{stop.title}</span>
                                <span className="text-xs text-slate-500 flex-shrink-0 ml-2">
                                  {stop.times_played} plays {stop.drop_off_rate > 0 ? `| ${stop.drop_off_rate}% drop` : ''}
                                </span>
                              </div>
                              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${stop.drop_off_rate > 30 ? 'bg-red-400' : stop.drop_off_rate > 15 ? 'bg-amber-400' : 'bg-teal-400'}`}
                                  style={{ width: `${barWidth}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Hidden Waypoints Coverage — GPS-triggered secrets.
                        High off-track fire-rate = route needs clearer signage
                        or better audio cues in the preceding stop. */}
                    {tourDetail.hidden_waypoint_metrics && tourDetail.hidden_waypoint_metrics.length > 0 && (
                      <div className="mb-6 rounded-xl bg-indigo-50/60 border border-indigo-200 p-4" data-testid="hidden-waypoint-coverage">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-bold text-indigo-900 flex items-center gap-2">
                            🔒 Hidden Waypoints Coverage
                            <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-semibold">
                              {tourDetail.hidden_waypoint_metrics.length}
                            </span>
                          </h4>
                          <span className="text-xs text-indigo-600">
                            % of tour starters who triggered each waypoint
                          </span>
                        </div>
                        <p className="text-xs text-indigo-700/80 mb-3">
                          Off-track waypoints firing often → route needs clearer signage. Encouragement waypoints rarely firing → move closer to the route.
                        </p>
                        <div className="space-y-2">
                          {tourDetail.hidden_waypoint_metrics.map((wp) => {
                            const isWarn = wp.hidden_kind === 'warn';
                            const barColor = isWarn
                              ? (wp.fire_rate >= 20 ? 'bg-red-500' : wp.fire_rate >= 5 ? 'bg-amber-400' : 'bg-emerald-400')
                              : (wp.fire_rate >= 50 ? 'bg-emerald-500' : wp.fire_rate >= 20 ? 'bg-teal-400' : 'bg-slate-300');
                            const badge = isWarn ? 'bg-amber-100 text-amber-800' : 'bg-indigo-100 text-indigo-800';
                            const health = wp.health || 'healthy';
                            const healthPill = {
                              healthy: { cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: '✓ Healthy' },
                              never_fires: { cls: 'bg-slate-100 text-slate-700 border-slate-300', label: '💤 Never fires' },
                              fires_too_often: { cls: 'bg-red-100 text-red-800 border-red-200', label: '⚠️ Too permissive' },
                              unconfigured: { cls: 'bg-amber-100 text-amber-800 border-amber-200', label: '🔗 No window set' },
                              insufficient_data: { cls: 'bg-slate-50 text-slate-500 border-slate-200', label: '⏳ Too few starts' },
                            }[health];
                            return (
                              <div
                                key={wp.stop_id}
                                className="p-3 rounded-lg bg-white/70 border border-indigo-100"
                                data-testid={`waypoint-row-${wp.stop_id}`}
                              >
                                <div className="flex items-center gap-3">
                                  <span className={`w-7 h-7 rounded-full ${isWarn ? 'bg-amber-500' : 'bg-indigo-500'} text-white font-black flex items-center justify-center text-xs flex-shrink-0`}>
                                    {wp.label}
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-1 gap-2">
                                      <div className="flex items-center gap-2 min-w-0 flex-wrap">
                                        <span className="text-sm font-medium text-slate-800 truncate">{wp.title}</span>
                                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${badge} flex-shrink-0`}>
                                          {isWarn ? '⚠️ Off-track' : '✅ On-track'}
                                        </span>
                                        <span
                                          className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${healthPill.cls} flex-shrink-0`}
                                          data-testid={`waypoint-health-${wp.stop_id}`}
                                          title={wp.health_reason || ''}
                                        >
                                          {healthPill.label}
                                        </span>
                                      </div>
                                      <span className="text-xs font-semibold text-slate-600 flex-shrink-0">
                                        {wp.fire_rate}% ({wp.unique_travelers}/{tourDetail.core_metrics.total_starts})
                                      </span>
                                    </div>
                                    <div className="h-2 bg-white rounded-full overflow-hidden border border-indigo-100">
                                      <div
                                        className={`h-full rounded-full ${barColor}`}
                                        style={{ width: `${Math.min(100, wp.fire_rate)}%` }}
                                      ></div>
                                    </div>
                                    {/* Eligibility config legend (admin-only, visible here in the analytics) */}
                                    {(wp.trigger_after_stop_number != null || wp.expires_when_stop_number != null || wp.expires_after_seconds != null) && (
                                      <p className="text-[10px] text-slate-500 mt-1.5 font-mono">
                                        🔗 {wp.trigger_after_stop_number != null ? `after #${wp.trigger_after_stop_number}` : 'from start'}
                                        {' → '}
                                        {wp.expires_when_stop_number != null ? `expires #${wp.expires_when_stop_number}` : 'no stop expiry'}
                                        {wp.expires_after_seconds != null ? ` · ${wp.expires_after_seconds}s` : ''}
                                      </p>
                                    )}
                                    {/* Actionable reason when unhealthy */}
                                    {health !== 'healthy' && health !== 'insufficient_data' && wp.health_reason && (
                                      <p className={`text-[11px] mt-1.5 ${health === 'fires_too_often' ? 'text-red-700' : health === 'never_fires' ? 'text-slate-600' : 'text-amber-700'}`}>
                                        {wp.health_reason}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Entry Points & Devices */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <h4 className="font-bold text-slate-700 text-sm mb-2">How Users Find This Tour</h4>
                        {Object.keys(tourDetail.entry_points).length > 0 ? (
                          <div className="space-y-1">
                            {Object.entries(tourDetail.entry_points).map(([source, count]) => (
                              <div key={source} className="flex justify-between text-sm">
                                <span className="text-slate-600 capitalize">{source}</span>
                                <span className="font-bold text-slate-900">{count}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-slate-400 text-sm">No data yet</p>
                        )}
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-700 text-sm mb-2">Devices</h4>
                        {Object.keys(tourDetail.devices).length > 0 ? (
                          <div className="space-y-1">
                            {Object.entries(tourDetail.devices).map(([device, count]) => (
                              <div key={device} className="flex justify-between text-sm">
                                <span className="text-slate-600 capitalize">{device}</span>
                                <span className="font-bold text-slate-900">{count}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-slate-400 text-sm">No data yet</p>
                        )}
                      </div>
                    </div>

                    {/* Recent Sessions */}
                    {tourDetail.recent_sessions.length > 0 && (
                      <div>
                        <h4 className="font-bold text-slate-700 text-sm mb-2">Recent Users</h4>
                        <div className="space-y-1 max-h-40 overflow-y-auto">
                          {tourDetail.recent_sessions.map((sess, i) => (
                            <div key={i} className="flex justify-between text-sm bg-slate-50 rounded px-3 py-1">
                              <span className="text-slate-700">{sess.email}</span>
                              <span className="text-slate-400 text-xs">{new Date(sess.timestamp).toLocaleDateString()}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {selectedTour === tour.tour_id && detailLoading && (
                  <div className="bg-white border-2 border-teal-400 border-t-0 rounded-b-xl p-6 -mt-2 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-3 border-teal-500 border-t-transparent mx-auto"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Saved Reports Section */}
        <div className="mt-10">
          <button
            onClick={() => { setShowSavedReports(!showSavedReports); if (!showSavedReports) fetchSavedReports(); }}
            data-testid="toggle-saved-reports"
            className="flex items-center gap-2 text-lg font-bold text-slate-900 hover:text-indigo-600 transition-colors mb-4"
          >
            <FileDown className="w-5 h-5" />
            Saved Reports ({savedReports.length || '...'})
            {showSavedReports ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {showSavedReports && (
            <div className="space-y-3">
              {savedReports.length === 0 ? (
                <p className="text-slate-400 text-sm py-4">No saved reports yet. Generate an AI report from any tour above.</p>
              ) : (
                savedReports.map((report) => (
                  <div key={report.insight_id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div
                      className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                      onClick={() => setSelectedReport(selectedReport === report.insight_id ? null : report.insight_id)}
                    >
                      <div>
                        <h4 className="font-bold text-slate-900">{report.tour_title}</h4>
                        <p className="text-slate-500 text-xs">
                          {report.tour_city} &bull; Generated {new Date(report.generated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                        {report.data_snapshot && (
                          <p className="text-slate-400 text-xs mt-1">
                            {report.data_snapshot.total_starts} starts &bull; {report.data_snapshot.completion_rate}% completion &bull; {report.data_snapshot.unique_users} users
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Generate PDF from saved report
                            if (tourDetail && tourDetail.tour.tour_id === report.tour_id) {
                              generatePDFReport(tourDetail, report);
                            } else {
                              // Fetch tour detail first, then generate
                              axios.get(`${API}/admin/analytics/tour/${report.tour_id}`, {
                                headers: { Authorization: `Bearer ${token}` }
                              }).then(res => {
                                generatePDFReport(res.data, report);
                              }).catch(() => toast.error('Failed to generate PDF'));
                            }
                          }}
                          className="p-2 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-lg transition-colors"
                          title="Download PDF"
                        >
                          <FileDown className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteSavedReport(report.insight_id); }}
                          className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <span className="text-sm font-bold">x</span>
                        </button>
                        {selectedReport === report.insight_id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </div>
                    </div>
                    
                    {selectedReport === report.insight_id && (
                      <div className="border-t border-slate-100 p-4 bg-slate-50 space-y-3">
                        {report.executive_summary && (
                          <div>
                            <h5 className="font-semibold text-indigo-800 text-sm mb-1">Executive Summary</h5>
                            <p className="text-slate-700 text-sm">{report.executive_summary}</p>
                          </div>
                        )}
                        {report.key_findings?.length > 0 && (
                          <div>
                            <h5 className="font-semibold text-slate-800 text-sm mb-1">Key Findings</h5>
                            <ul className="text-slate-600 text-sm space-y-1">
                              {report.key_findings.map((f, i) => <li key={i}>- {f}</li>)}
                            </ul>
                          </div>
                        )}
                        {report.revenue_opportunities?.length > 0 && (
                          <div>
                            <h5 className="font-semibold text-green-800 text-sm mb-1">Revenue Opportunities</h5>
                            <ul className="text-green-700 text-sm space-y-1">
                              {report.revenue_opportunities.map((r, i) => <li key={i}>$ {r}</li>)}
                            </ul>
                          </div>
                        )}
                        {report.experience_improvements?.length > 0 && (
                          <div>
                            <h5 className="font-semibold text-blue-800 text-sm mb-1">Experience Improvements</h5>
                            <ul className="text-blue-700 text-sm space-y-1">
                              {report.experience_improvements.map((e, i) => <li key={i}>- {e}</li>)}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

      </main>
    </div>
  );
};

export default AnalyticsDashboard;
